import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { ChapterLabel } from "./ChapterLabel";
import { Reveal } from "./Reveal";
import { scrollToSection } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: "og_website",
    name: "Website or App",
    price: "$500",
    cadence: "one-time + $50/mo",
    tagline: "Just a website or app — built and managed for you.",
    bullets: [
      "Custom website or app design & build",
      "Mobile-first, fast-loading design",
      "Hosting, security & maintenance handled",
      "Content updates every month",
      "No audits or reporting included",
    ],
    cta: "Start My Build",
    featured: false,
  },
  {
    id: "og_monthly",
    name: "Monthly",
    price: "$199",
    cadence: "CAD / month",
    tagline: "The full service — website included.",
    bullets: [
      "Website design & build included",
      "Ongoing website management & updates",
      "Monthly website performance check",
      "Google ranking & Business Profile report",
      "CRM, lead tracking & speed-to-lead review",
      "Monthly walkthrough call",
    ],
    cta: "Start Monthly",
    featured: true,
  },
  {
    id: "og_annual",
    name: "Annual",
    price: "$2,000",
    cadence: "CAD / year",
    tagline: "Everything in Monthly — one payment a year.",
    bullets: [
      "Everything in the Monthly plan",
      "Website design & build included",
      "Ongoing website management & updates",
      "Two months free vs paying monthly",
      "Priority support all year",
    ],
    cta: "Pay Yearly & Save",
    featured: false,
  },
];

export const Pricing = () => {
  const [busy, setBusy] = useState(null);

  const startCheckout = async (planId) => {
    setBusy(planId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBusy(null);
    }
  };

  return (
    <section id="pricing" className="py-24 sm:py-32 bg-[#0B162B]/40" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <ChapterLabel text="Chapter 06 // Pricing" />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white max-w-3xl">
            Simple Pricing, Website Included
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-2xl">
            Every plan is built and managed by us. Pick monthly, save with annual, or just get a website or app.
          </p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan, i) => (
            <Reveal key={plan.id} delay={0.1 + i * 0.08} className="h-full">
              <div
                data-testid={`pricing-plan-${plan.id}`}
                className={`relative flex h-full flex-col rounded-3xl border p-8 overflow-hidden ${
                  plan.featured
                    ? "border-white/30 bg-[#0F1B31] lg:-translate-y-3 shadow-[0_20px_60px_-20px_rgba(255,255,255,0.15)]"
                    : "border-white/10 bg-[#0F1B31]/60"
                }`}
              >
                {plan.featured && (
                  <>
                    <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-white/[0.06] blur-3xl" />
                    <p className="absolute top-5 right-5 rounded-full bg-white px-3 py-1 font-mono-label text-[10px] uppercase tracking-[0.2em] text-[#060D1A]">
                      Most Popular
                    </p>
                  </>
                )}
                <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400">
                  {plan.name}
                </p>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-display text-5xl font-extrabold tracking-tight text-white leading-none">
                    {plan.price}
                  </span>
                  <span className="font-mono-label text-[11px] uppercase tracking-[0.15em] text-slate-400 pb-1.5">
                    {plan.cadence}
                  </span>
                </div>
                <p className="mt-3 text-sm text-slate-400">{plan.tagline}</p>
                <ul className="mt-7 space-y-3 flex-1">
                  {plan.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[#060D1A]">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${plan.id}`}
                  onClick={() => startCheckout(plan.id)}
                  disabled={busy !== null}
                  className={`group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-colors duration-300 disabled:opacity-50 ${
                    plan.featured
                      ? "bg-white text-[#060D1A] hover:bg-slate-300"
                      : "border border-white/20 text-white hover:bg-white hover:text-[#060D1A]"
                  }`}
                >
                  {busy === plan.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  )}
                  {busy === plan.id ? "Opening secure checkout…" : plan.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.35}>
          <p className="mt-10 text-sm text-slate-500" data-testid="pricing-build-note">
            Not sure which fits?{" "}
            <button
              onClick={() => scrollToSection("#contact-form")}
              data-testid="pricing-free-call-link"
              className="text-slate-300 underline underline-offset-4 decoration-white/30 transition-colors duration-300 hover:text-white"
            >
              Book a free call
            </button>{" "}
            and we'll figure it out together. Secure checkout by Stripe — no contracts on monthly.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
